/**
 * Calculate estimated time of arrival (ETA) in minutes based on city and distance
 * @param cityId - The city identifier (DAD, SGN, HAN, etc.)
 * @param distance - Distance in meters
 * @returns ETA in minutes or null if cannot calculate
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function calculateETA(
  cityId: string | null,
  distance: number
): number | null {
  if (!cityId || !distance) return null;

  const km = distance / 1000;
  let mins = 0;

  switch (cityId) {
    case 'DAD':
      if (km < 2) mins = 15;
      else if (km < 3) mins = 20;
      else mins = 25;
      break;
    case 'SGN':
      if (km < 2) mins = 20;
      else if (km < 3) mins = 25;
      else mins = 35;
      break;
    case 'HAN':
      if (km < 2) mins = 25;
      else if (km < 3) mins = 30;
      else mins = 35;
      break;
    case 'VCL':
    case 'DNI':
    case 'HDG':
      if (km < 2) mins = 10;
      else if (km < 3) mins = 15;
      else mins = 20;
      break;
    default:
      return null;
  }

  return mins;
}

/**
 * Format distance from meters to kilometers with 1 decimal place
 * @param distance - Distance in meters
 * @returns Formatted distance string (e.g., "2.5 km")
 */
export function formatDistance(distance: number): string {
  return `${(distance / 1000).toFixed(1)} km`;
}

/**
 * Format distance with meter/kilometer logic like SoloMealItemCard
 * @param distance - Distance in meters
 * @returns Formatted distance string (e.g., "500m" or "2.5km")
 */
export function formatDistanceMeterKilometer(distance: number): string {
  return distance < 1000
    ? `${Math.round(distance)}m`
    : `${(distance / 1000).toFixed(1)}km`;
}

/**
 * Get ETA display text with distance and time
 * @param cityId - The city identifier
 * @param distance - Distance in meters
 * @returns Formatted ETA text or null if cannot calculate
 */
export function getETADisplayText(
  cityId: string | null,
  distance: number
): string | null {
  const etaMinutes = calculateETA(cityId, distance);
  if (etaMinutes === null) return null;

  const distanceText = formatDistance(distance);
  return `${distanceText} • ${etaMinutes} phút trở lên`;
}
